import { ApiProjects } from './projects';
import {
  Owner,
  Fee,
  MarkPaidSettlement,
  Payment,
  Expense,
  Revenue,
  Statement,
  CondoDocument,
  DashboardStats,
  QuarterlyReport,
  OwnerDashboard,
  Unit,
  UnitFilters,
  UnitStatus,
} from '../../types';
import { apiFetch } from './base';

export class ApiCoreEntities extends ApiProjects {
  // ============ UNITS MANAGEMENT ============
  
  /**
   * Get all units, optionally filtered by condo
   */
  async getUnits(condoId?: string): Promise<Unit[]> {
    const url = condoId ? `/units/condoId/${condoId}` : '/units';
    return this.request<Unit[]>(url);
  }

  /**
   * Get a single unit by ID
   */
  async getUnit(id: string): Promise<Unit> {
    return this.request<Unit>(`/units/${id}`);
  }

  /**
   * Get units by condo ID
   */
  async getUnitsByCondo(condoId: string): Promise<Unit[]> {
    return this.request<Unit[]>(`/units/condo/${condoId}`);
  }

  /**
   * Create a new unit
   */
  async createUnit(unit: Omit<Unit, 'id'>): Promise<Unit> {
    return this.request<Unit>('/units', {
      method: 'POST',
      body: JSON.stringify(unit),
    });
  }

  /**
   * Update an existing unit
   */
  async updateUnit(id: string, unit: Partial<Unit>): Promise<Unit> {
    return this.request<Unit>(`/units/${id}`, {
      method: 'PUT',
      body: JSON.stringify(unit),
    });
  }

  /**
   * Delete a unit
   */
  async deleteUnit(id: string): Promise<void> {
    return this.request<void>(`/units/${id}`, {
      method: 'DELETE',
    });
  }

  /**
   * Get units by status (available, occupied, maintenance, reserved)
   */
  async getUnitsByStatus(status: UnitStatus, condoId?: string): Promise<Unit[]> {
    const url = condoId 
      ? `/units/status/${status}?condoId=${condoId}`
      : `/units/status/${status}`;
    return this.request<Unit[]>(url);
  }

  /**
   * Get available units
   */
  async getAvailableUnits(condoId?: string): Promise<Unit[]> {
    return this.getUnitsByStatus('available', condoId);
  }

  /**
   * Get occupied units
   */
  async getOccupiedUnits(condoId?: string): Promise<Unit[]> {
    return this.getUnitsByStatus('occupied', condoId);
  }

  /**
   * Get units under maintenance
   */
  async getMaintenanceUnits(condoId?: string): Promise<Unit[]> {
    return this.getUnitsByStatus('maintenance', condoId);
  }

  /**
   * Get units by floor
   */
  async getUnitsByFloor(floor: number, condoId?: string): Promise<Unit[]> {
    const url = condoId 
      ? `/units/floor/${floor}?condoId=${condoId}`
      : `/units/floor/${floor}`;
    return this.request<Unit[]>(url);
  }

  /**
   * Filter units with advanced criteria
   */
  async filterUnits(filters: UnitFilters, condoId?: string): Promise<Unit[]> {
    const queryParams = new URLSearchParams();
    
    if (filters.status?.length) {
      filters.status.forEach(status => queryParams.append('status', status));
    }
    if (filters.minFloor !== undefined) queryParams.append('minFloor', filters.minFloor.toString());
    if (filters.maxFloor !== undefined) queryParams.append('maxFloor', filters.maxFloor.toString());
    if (filters.minArea !== undefined) queryParams.append('minArea', filters.minArea.toString());
    if (filters.maxArea !== undefined) queryParams.append('maxArea', filters.maxArea.toString());
    if (filters.bedrooms !== undefined) queryParams.append('bedrooms', filters.bedrooms.toString());
    if (filters.bathrooms !== undefined) queryParams.append('bathrooms', filters.bathrooms.toString());
    if (filters.hasOwner !== undefined) queryParams.append('hasOwner', filters.hasOwner.toString());
    if (condoId) queryParams.append('condoId', condoId);

    const url = `/units/filter?${queryParams.toString()}`;
    return this.request<Unit[]>(url);
  }

  /**
   * Assign owner to unit (automatically updates unit status to occupied)
   */
  async assignOwnerToUnit(unitId: string, ownerId: string): Promise<Unit> {
    return this.request<Unit>(`/units/${unitId}/assign-owner`, {
      method: 'POST',
      body: JSON.stringify({ ownerId }),
    });
  }

  /**
   * Remove owner from unit (automatically updates unit status to available)
   */
  async removeOwnerFromUnit(unitId: string): Promise<Unit> {
    return this.request<Unit>(`/units/${unitId}/remove-owner`, {
      method: 'POST',
    });
  }

  /**
   * Update unit status
   */
  async updateUnitStatus(unitId: string, status: UnitStatus): Promise<Unit> {
    return this.request<Unit>(`/units/${unitId}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    });
  }

  /**
   * Get unit statistics
   */
  async getUnitStats(condoId?: string): Promise<{
    totalUnits: number;
    occupiedUnits: number;
    vacantUnits: number;
    maintenanceUnits: number;
    occupancyRate: number;
    unitsByFloor: Array<{ floor: number; count: number; occupied: number }>;
    unitsByBedrooms: Array<{ bedrooms: number; count: number; occupied: number }>;
    averageUnitSize: number;
  }> {
    const url = condoId ? `/units/stats?condoId=${condoId}` : '/units/stats';
    return this.request(url);
  }

  /**
   * Get units with no owner assigned
   */
  async getUnassignedUnits(condoId?: string): Promise<Unit[]> {
    const url = condoId ? `/units/unassigned?condoId=${condoId}` : '/units/unassigned';
    return this.request<Unit[]>(url);
  }

  /**
   * Get unit by unit number (within a condo).
   *
   * Sprint 94.5 — previously caught ALL errors and returned null, so a 401/403
   * (e.g. tenancy guard denies the condo) looked identical to "no such unit".
   * Now auth/permission failures propagate so the UI can show an access-denied
   * state; only a genuine 404 (unit number not found) resolves to null.
   */
  async getUnitByNumber(unitNumber: string, condoId: string): Promise<Unit | null> {
    const res = await apiFetch(`/units/condo/${condoId}/number/${unitNumber}`);
    if (res.status === 404) return null;
    if (!res.ok) {
      const body = await res.json().catch(() => ({} as any));
      const requestId = res.headers.get('X-Request-Id');
      const tail = requestId ? ` (id: ${requestId})` : '';
      throw new Error((body.detail || body.message || body.title || `HTTP ${res.status}`) + tail);
    }
    if (res.status === 204) return null;
    return (await res.json().catch(() => null)) as Unit | null;
  }

  /**
   * Bulk create units (useful for initializing a new condo)
   */
  async bulkCreateUnits(units: Omit<Unit, 'id'>[]): Promise<Unit[]> {
    return this.request<Unit[]>('/units/bulk', {
      method: 'POST',
      body: JSON.stringify({ units }),
    });
  }

  /**
   * Get payment history for a specific unit
   */
  async getUnitPaymentHistory(unitId: string): Promise<Payment[]> {
    return this.request<Payment[]>(`/units/${unitId}/payments`);
  }

  /**
   * Get owner of a unit
   */
  async getUnitOwner(unitId: string): Promise<Owner | null> {
    try {
      return await this.request<Owner>(`/units/${unitId}/owner`);
    } catch (error) {
      return null;
    }
  }

  // ============ END UNITS MANAGEMENT ============

  // Owners endpoints (with condo filtering)
  async getOwners(condoId?: string): Promise<Owner[]> {
    const url = condoId ? `/owners/condo/${condoId}` : '/owners';
    return this.request<Owner[]>(url);
  }

  async getOwner(id: string): Promise<Owner> {
    return this.request<Owner>(`/owners/${id}`);
  }

  async createOwner(owner: Omit<Owner, 'id'>): Promise<Owner> {
    return this.request<Owner>('/owners', {
      method: 'POST',
      body: JSON.stringify(owner),
    });
  }

  async updateOwner(id: string, owner: Partial<Owner>): Promise<Owner> {
    return this.request<Owner>(`/owners/${id}`, {
      method: 'PUT',
      body: JSON.stringify(owner),
    });
  }

  async deleteOwner(id: string): Promise<void> {
    return this.request<void>(`/owners/${id}`, {
      method: 'DELETE',
    });
  }

  // Fees endpoints (with condo filtering)
  async getFees(condoId?: string): Promise<Fee[]> {
    // Sprint 81 (ADR-081) — migrate the legacy global `/fees?condoId=` path
    // (403 under Sprint 61/76 tenancy hardening, masked as $0.00) to the
    // scoped `/fees/condo/{condoId}` path the lockdown left open.
    const url = condoId ? `/fees/condo/${condoId}` : '/fees';
    return this.request<Fee[]>(url);
  }

  async getFee(id: string): Promise<Fee> {
    return this.request<Fee>(`/fees/${id}`);
  }

  async createFee(fee: Omit<Fee, 'id'>): Promise<Fee> {
    return this.request<Fee>('/fees', {
      method: 'POST',
      body: JSON.stringify(fee),
    });
  }

  async updateFee(id: string, fee: Partial<Fee>): Promise<Fee> {
    return this.request<Fee>(`/fees/${id}`, {
      method: 'PUT',
      body: JSON.stringify(fee),
    });
  }

  // Sprint 135 (R12-2, ADR-130) — remove a whole condo-wide assessment. A
  // condo-wide fee is N per-owner rows sharing a groupId; deleting the
  // representative row alone left the other N-1 owners charged.
  async deleteFeeGroup(groupId: string): Promise<void> {
    return this.request<void>(`/fees/group/${groupId}`, { method: 'DELETE' });
  }

  // Sprint 134 (R12-1, ADR-129) — `settlement` is optional so the owner "Pay Now"
  // path keeps posting no body (server defaults: method MANUAL, no owner check).
  // The manager's manual-payment form sends both: `ownerId` is the CANONICAL
  // login-user UUID (Owner.userId), not the numeric Owner.id, and acts as a
  // confirmation — a mismatch against the fee's real owner is rejected with 400
  // rather than silently re-attributed.
  async markFeeAsPaid(id: string, settlement?: MarkPaidSettlement): Promise<Fee> {
    return this.request<Fee>(`/fees/${id}/mark-paid`, {
      method: 'POST',
      ...(settlement ? { body: JSON.stringify(settlement) } : {}),
    });
  }

  async deleteFee(id: string): Promise<void> {
    return this.request<void>(`/fees/${id}`, {
      method: 'DELETE',
    });
  }

  // Payments endpoints (with condo filtering)
  async getPayments(condoId?: string): Promise<Payment[]> {
    // Sprint 32 — finding R8. The no-condoId path used to hit
    // GET /api/payments, which returns 501 Not Implemented (Sprint 25
    // deleted the IDOR-shaped owner/condo stubs but kept this one as a
    // placeholder). Dashboard / store load fires this on every login
    // before currentCondo resolves, producing a 501 in the network panel
    // and noise in the backend log. Short-circuit when condoId is
    // missing — the dashboard re-fires the call once currentCondo is
    // set, so no real data path is lost.
    if (!condoId) return [];
    return this.request<Payment[]>(`/payments?condoId=${condoId}`);
  }

  async createPayment(payment: Omit<Payment, 'id'>): Promise<Payment> {
    return this.request<Payment>('/payments', {
      method: 'POST',
      body: JSON.stringify(payment),
    });
  }

  // Expenses endpoints (with condo filtering)
  // R11-3 — `GET /expenses?condoId=` routed to the UNSCOPED cross-tenant list,
  // which is deliberately SUPER_ADMIN-only (Sprint 119 / ADR-112), so condo
  // admins got 403 and the Expenses UI silently showed "No data". The
  // condo-scoped path variant is the one every condo-bound role may read.
  async getExpenses(condoId?: string): Promise<Expense[]> {
    const url = condoId ? `/expenses/condo/${condoId}` : '/expenses';
    return this.request<Expense[]>(url);
  }

  async createExpense(expense: Omit<Expense, 'id'>): Promise<Expense> {
    return this.request<Expense>('/expenses', {
      method: 'POST',
      body: JSON.stringify(expense),
    });
  }

  async updateExpense(id: string, expense: Partial<Expense>): Promise<Expense> {
    return this.request<Expense>(`/expenses/${id}`, {
      method: 'PUT',
      body: JSON.stringify(expense),
    });
  }

  async deleteExpense(id: string): Promise<void> {
    return this.request<void>(`/expenses/${id}`, {
      method: 'DELETE',
    });
  }

  // Revenue endpoints (with condo filtering)
  async getRevenues(condoId?: string): Promise<Revenue[]> {
    const url = condoId ? `/revenues?condoId=${condoId}` : '/revenues';
    return this.request<Revenue[]>(url);
  }

  async createRevenue(revenue: Omit<Revenue, 'id'>): Promise<Revenue> {
    return this.request<Revenue>('/revenues', {
      method: 'POST',
      body: JSON.stringify(revenue),
    });
  }

  // Statements endpoints (with condo filtering)
  // Backed by the server-side StatementController (GET /api/statements?condoId=),
  // which DERIVES statements from the condo's fees (no statements table). Before
  // that endpoint existed this 404'd and the Statements page computed them
  // client-side via condoStore.generateStatements. We keep a graceful fallback:
  // a "genuinely empty" result (404 not-found / 200 with an empty body) resolves
  // to [] so the page renders empty and the user can still hit "Generate".
  //
  // Sprint 94.5 — but we MUST NOT swallow auth/permission failures: a 401/403
  // (e.g. tenancy guard denies the condo) previously fell into the same []
  // path and rendered as "no statements", masking an access-denied state. Now
  // we let auth errors propagate so the UI can surface the real condition,
  // while still treating not-found / empty as an empty list.
  async getStatements(condoId?: string): Promise<Statement[]> {
    const url = condoId ? `/statements?condoId=${condoId}` : '/statements';
    return this.fetchListGraceful<Statement>(url);
  }

  async createStatement(statement: Omit<Statement, 'id'>): Promise<Statement> {
    return this.request<Statement>('/statements', {
      method: 'POST',
      body: JSON.stringify(statement),
    });
  }

  // Dashboard Stats endpoints
  async getDashboardStats(condoId?: string): Promise<DashboardStats> {
    const url = condoId ? `/dashboard/stats?condoId=${condoId}` : '/dashboard/stats';
    return this.request<DashboardStats>(url);
  }

  async getDashboardOverview(condoId?: string): Promise<{
    totalRevenue: number;
    totalExpenses: number;
    netIncome: number;
    feeCollectionRate: number;
    occupancyRate: number;
    pendingFees: number;
    overdueFees: number;
  }> {
    const url = condoId ? `/dashboard/overview?condoId=${condoId}` : '/dashboard/overview';
    return this.request(url);
  }

  async getFinancialSummary(condoId?: string): Promise<{
    currentQuarter: {
      revenue: number;
      expenses: number;
      netIncome: number;
    };
    previousQuarter: {
      revenue: number;
      expenses: number;
      netIncome: number;
    };
    yearToDate: {
      revenue: number;
      expenses: number;
      netIncome: number;
    };
  }> {
    const url = condoId ? `/dashboard/financial-summary?condoId=${condoId}` : '/dashboard/financial-summary';
    return this.request(url);
  }

  async getCollectionMetrics(condoId?: string): Promise<{
    totalFees: number;
    collectedAmount: number;
    pendingAmount: number;
    overdueAmount: number;
    collectionRate: number;
    averageCollectionTime: number;
  }> {
    const url = condoId ? `/dashboard/collection-metrics?condoId=${condoId}` : '/dashboard/collection-metrics';
    return this.request(url);
  }

  
  async getOwnerMetrics(condoId?: string): Promise<{
    totalOwners: number;
    activeOwners: number;
    newOwnersThisMonth: number;
    occupancyRate: number;
    averageTenure: number;
  }> {
    const url = condoId ? `/dashboard/owner-metrics?condoId=${condoId}` : '/dashboard/owner-metrics';
    return this.request(url);
  }

  async getExpenseBreakdown(condoId?: string): Promise<{
    byCategory: Array<{
      category: string;
      amount: number;
      percentage: number;
    }>;
    byMonth: Array<{
      month: string;
      amount: number;
    }>;
    topVendors: Array<{
      vendor: string;
      amount: number;
    }>;
  }> {
    const url = condoId ? `/dashboard/expense-breakdown?condoId=${condoId}` : '/dashboard/expense-breakdown';
    return this.request(url);
  }

  async getRevenueBreakdown(condoId?: string): Promise<{
    bySource: Array<{
      source: string;
      amount: number;
      percentage: number;
    }>;
    byMonth: Array<{
      month: string;
      amount: number;
    }>;
  }> {
    const url = condoId ? `/dashboard/revenue-breakdown?condoId=${condoId}` : '/dashboard/revenue-breakdown';
    return this.request(url);
  }

  async getRecentActivity(condoId?: string): Promise<{
    recentPayments: Array<{
      id: string;
      ownerName: string;
      amount: number;
      date: string;
      method: string;
    }>;
    recentExpenses: Array<{
      id: string;
      description: string;
      amount: number;
      date: string;
      vendor?: string;
    }>;
    upcomingFees: Array<{
      id: string;
      ownerName: string;
      amount: number;
      dueDate: string;
    }>;
  }> {
    const url = condoId ? `/dashboard/recent-activity?condoId=${condoId}` : '/dashboard/recent-activity';
    return this.request(url);
  }

  async getMaintenanceMetrics(condoId?: string): Promise<{
    openRequests: number;
    completedThisMonth: number;
    averageResolutionTime: number;
    byPriority: Array<{
      priority: string;
      count: number;
    }>;
    byStatus: Array<{
      status: string;
      count: number;
    }>;
  }> {
    const url = condoId ? `/dashboard/maintenance-metrics?condoId=${condoId}` : '/dashboard/maintenance-metrics';
    return this.request(url);
  }

  // Quarterly Reports (with condo filtering)
  async getQuarterlyReport(quarter: number, year: number, condoId?: string): Promise<QuarterlyReport> {
    const url = condoId 
      ? `/reports/quarterly?quarter=${quarter}&year=${year}&condoId=${condoId}`
      : `/reports/quarterly?quarter=${quarter}&year=${year}`;
    return this.request<QuarterlyReport>(url);
  }

  // Owner Dashboard
  async getOwnerDashboard(ownerId: string): Promise<OwnerDashboard> {
    return this.request<OwnerDashboard>(`/dashboard/owner/${ownerId}`);
  }

  // Documents (with condo filtering)
  async getDocuments(condoId?: string): Promise<CondoDocument[]> {
    const url = condoId ? `/documents?condoId=${condoId}` : '/documents';
    return this.request<CondoDocument[]>(url);
  }

  async createDocument(document: Omit<CondoDocument, 'id'>): Promise<CondoDocument> {
    return this.request<CondoDocument>('/documents', {
      method: 'POST',
      body: JSON.stringify(document),
    });
  }

  async deleteDocument(id: string): Promise<void> {
    return this.request<void>(`/documents/${id}`, {
      method: 'DELETE',
    });
  }

  /**
   * Fetch a document's stored file as an authenticated Blob.
   *
   * QA F2/F3 — "View" / "Download" used to point the browser straight at
   * the raw storage path (e.g. http://host/api/documents/files/foo.pdf) in a
   * new tab. That request carries no Authorization header, so the protected
   * DocumentController (@PreAuthorize at class level) answered 401. Mirror the
   * downloadAgmPacket pattern: go through apiFetch so the bearer token is
   * attached, then hand the caller a Blob to turn into an object URL.
   *
   * `fileUrl` is persisted by the backend as "/api/documents/files/<name>".
   * apiFetch already prepends the /api base, so strip the leading "/api"
   * before passing the path through (otherwise we'd hit /api/api/...).
   */
  async downloadDocumentFile(fileUrl: string): Promise<Blob> {
    if (!fileUrl) throw new Error('Document has no file attached');
    // Normalize to a path apiFetch can consume (it adds the /api base + auth).
    let path = fileUrl;
    if (/^https?:\/\//i.test(path)) {
      // Absolute URL — reduce to the pathname so apiFetch's base + auth apply.
      try { path = new URL(path).pathname; } catch { /* leave as-is */ }
    }
    if (path.startsWith('/api/')) path = path.slice(4); // drop leading '/api'
    else if (!path.startsWith('/')) path = '/' + path;
    const res = await apiFetch(path);
    if (!res.ok) throw new Error(`Document download failed: ${res.status}`);
    return res.blob();
  }

  async uploadDocument(formData: FormData): Promise<CondoDocument> {
    // Sprint 20 — was hand-rolling fetch with a manual Authorization header.
    // Now goes through apiFetch so it picks up the timeout, X-Request-Id
    // header, and 401 → silent-refresh-and-retry path that every other
    // call in this file uses. apiFetch detects the FormData body and
    // skips the Content-Type override so the multipart boundary is
    // browser-managed.
    const response = await apiFetch('/documents/upload', {
      method: 'POST',
      body: formData,
    });
    if (!response.ok) {
      throw new Error('Upload failed');
    }
    return response.json();
  }
  

  // Send statement email
  async sendStatementEmail(statementId: string): Promise<void> {
    return this.request<void>(`/statements/${statementId}/send`, {
      method: 'POST',
    });
  }

  // Send bulk statements
  async sendBulkStatements(period: string, condoId?: string): Promise<{ statementId: string; success: boolean; error?: string }[]> {
    const url = condoId 
      ? `/statements/bulk-send?period=${period}&condoId=${condoId}`
      : `/statements/bulk-send?period=${period}`;
    return this.request(url, {
      method: 'POST',
    });
  }
}
